import React, { useState } from 'react';
import { useMutation } from 'react-query';
import * as API from 'api';

import AuthAxios from 'utils/authAxios';
import { dataApiUrl } from 'utils/url';

import { withStyles } from '@material-ui/core/styles';
import MuiIconButton from '@material-ui/core/IconButton';
import MuiBox from '@material-ui/core/Box';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiCollapse from '@material-ui/core/Collapse';
import MuiKeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import MuiKeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import MuiTypography from '@material-ui/core/Typography';
import MuiChip from '@material-ui/core/Chip';

import SelectGroup from './SelectGroup';
import EditUPC from './edit-UPC';
import RowButtons from './RowButtons';

import TrackTable from './track-table';
import ReleaseDistributionStatus from './release-distribution-status';
import ReleaseDetailsDialog from './release-details-dialog';
import MuiTooltip from '@material-ui/core/Tooltip';
import MuiInfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
const Releases = API.Releases();

const DataApiUrl = dataApiUrl();

const styles = () => ({
  root: {
    '& > *': {
      borderBottom: 'unset',
    },
  },
  edit: {
    fontSize: 'medium',
  },
  expandedRow: {
    backgroundColor: '#f3f3f3',
  },
  loadWrapper: {
    display: 'flex',
    justifyContent: 'center',
    margin: '1rem 0',
  },
  buttonCell: {
    whiteSpace: 'nowrap',
  },
  totalsTable: {
    marginTop: '1rem',
  },
  fileCell: {
    maxWidth: '300px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  totalsCell: {
    border: 0,
    fontWeight: 'bold',
  },
  error: {
    color: 'red',
  },
});

const Row = props => {
  const { classes, release: _release, editing, onEditChange, releaseChanged, groups, isRefetching } = props;

  const release = createRowData(_release);
  const distributionJobs = _release.distributionJobs;
  const [isExpanded, setIsExpanded] = useState(false);
  const [changingGroup, setChangingGroup] = useState(false);
  const [changingUPC, setChangingUPC] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const { mutate: changeUPC } = useMutation(({ releaseId, upc }) => requestChangeUPC(releaseId, upc), {
    onSuccess: () => {
      setChangingUPC(false);
      releaseChanged();
    },
    onError: error => {
      alert(error.response?.data?.error);
    },
  });

  const {
    reset: resetArchive,
    mutate: toggleArchive,
    isLoading: isArchiving,
  } = useMutation(({ id, is_hidden }) => Releases.patchRelease(id, { is_hidden }), {
    onSuccess: releaseChanged,
    onError: error => {
      alert(error);
      resetArchive();
    },
  });

  const { isLoading: isCloning, mutate: cloneRelease } = useMutation(releaseId => Releases.clone(releaseId), {
    onSuccess: releaseChanged,
    onError: e => alert(e),
  });

  const { mutate: handleChangeReleaseGroup } = useMutation(
    ({ releaseId, groupId }) =>
      AuthAxios.patch(`${DataApiUrl}/admin/releases/${releaseId}/changeOwnership`, {
        user_group_id: groupId,
      }),
    {
      onSuccess: () => {
        setChangingGroup(false);
        releaseChanged();
      },
      onError: e => alert(e),
    }
  );

  const onArchiveToggle = () =>
    toggleArchive({
      id: release.id,
      is_hidden: !release.isArchived,
    });

  //DB Data
  const releaseHasTracks = !!release.tracks.length;

  return (
    <>
      <MuiTableRow className={classes.root}>
        <MuiTableCell component="th" scope="release">
          {release.id}
        </MuiTableCell>
        <MuiTableCell>
          {!changingGroup && release.user_group}
          {changingGroup && (
            <SelectGroup
              onSelect={handleChangeReleaseGroup}
              releaseId={release.id}
              groups={groups}
              currentGroup={groups.find(g => g.name === release.user_group_id)}
            />
          )}
        </MuiTableCell>
        <MuiTableCell>{release.display_artist_name}</MuiTableCell>
        <MuiTableCell>
          {release.title}
          {release.isAepComanager && (
            <MuiChip
              size="small"
              color="primary"
              variant="outlined"
              label="aep-comanager"
              style={{ marginLeft: 8 }}
              title="Created through the aep-comanager API"
            />
          )}
        </MuiTableCell>
        <MuiTableCell>
          {!changingUPC && release.asset_release_upc}
          {changingUPC && <EditUPC upc={release.asset_release_upc} releaseId={release.id} onSave={changeUPC} />}
        </MuiTableCell>
        <MuiTableCell>{release.status}</MuiTableCell>
        <MuiTableCell style={{ width: '20%' }} className={classes.buttonCell} align="right">
          <RowButtons
            onCloneClick={() => cloneRelease(release.id)}
            //normally it just toggles, but if setFalse is set it will just set to false
            onChangeReleaseGroup={setFalse => setChangingGroup(setFalse ? false : !changingGroup)}
            onChangeUPCClicked={setFalse => setChangingUPC(setFalse ? false : !changingUPC)}
            isCloning={isCloning}
            isRefetching={isRefetching}
            editing={editing}
            onEditChange={editing => onEditChange(editing ? release.id : null)}
            isArchived={release.isArchived}
            onArchiveToggle={onArchiveToggle}
            isArchiving={isArchiving}
            status={release.status}
          />
          <MuiTooltip title="Details">
            <MuiIconButton aria-label="release details" onClick={() => setIsDetailsOpen(true)}>
              <MuiInfoOutlinedIcon />
            </MuiIconButton>
          </MuiTooltip>
          <MuiIconButton aria-label="expand release" onClick={() => setIsExpanded(!isExpanded)} tabIndex={-1}>
            {isExpanded ? <MuiKeyboardArrowUpIcon /> : <MuiKeyboardArrowDownIcon />}
          </MuiIconButton>
        </MuiTableCell>
      </MuiTableRow>
      <ReleaseDetailsDialog open={isDetailsOpen} onClose={() => setIsDetailsOpen(false)} releaseId={release.id} />
      <MuiTableRow className={classes.expandedRow}>
        <MuiTableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <MuiCollapse in={isExpanded} timeout="auto" unmountOnExit>
            <MuiBox my={1}>
              <MuiTypography variant="h6" gutterBottom component="div">
                Tracks
              </MuiTypography>
              <>
                {releaseHasTracks ? (
                  <TrackTable
                    release={release}
                    isDelivered={release.status === 'Delivered'}
                    releaseChanged={releaseChanged}
                  />
                ) : (
                  'No tracks associated with this release'
                )}
              </>
            </MuiBox>
            <ReleaseDistributionStatus release={release} distributionJobs={distributionJobs} />
          </MuiCollapse>
        </MuiTableCell>
      </MuiTableRow>
    </>
  );
};

async function requestChangeUPC(releaseId, upc) {
  return AuthAxios.patch(`${DataApiUrl}/admin/releases/${releaseId}/changeUPC`, {
    asset_release_upc: upc,
  });
}

export default withStyles(styles)(Row);

function createRowData(release) {
  const flattenedTracks =
    release?.set
      ?.map(set => {
        return set?.tracks.map(track => {
          return { ...track.trackParties, ...track, sequence: track.sequence };
        });
      })
      .flat() ?? [];

  return {
    id: release.id,
    user_group: release.userGroup?.name,
    display_artist_name: release.display_artist_name,
    title: release.title || '[Untitled]',
    asset_release_upc: release.asset_release_upc,
    status: release.status,
    tracks: flattenedTracks,
    isArchived: release.is_hidden,
    isAepComanager: !!release.is_aep_comanager,
  };
}
